import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Edit, Trash2, Mail, Settings, Database, Wand2, CheckCircle2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

interface BrevoFieldMapping {
  id: string;
  userField: string;
  brevoAttribute: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function BrevoMappingManager() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<BrevoFieldMapping | null>(null);
  const [templateIds, setTemplateIds] = useState<Record<string, number> | null>(null);

  const createTemplatesMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/brevo/create-automation-templates");
      return res.json();
    },
    onSuccess: (data) => {
      setTemplateIds(data.templateIds);
      toast({
        title: "Templates created",
        description: `4 automation email templates created in Brevo.`,
      });
    },
    onError: () => {
      toast({ title: "Failed", description: "Could not create templates. Check BREVO_API_KEY.", variant: "destructive" });
    },
  });

  const [stripeWebhookResults, setStripeWebhookResults] = useState<{ id: string; url: string; alreadyHad: boolean; updated: boolean }[] | null>(null);
  const addPaymentFailedMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/stripe/add-payment-failed-event");
      return res.json();
    },
    onSuccess: (data) => {
      setStripeWebhookResults(data.results);
      const updatedCount = data.results.filter((r: any) => r.updated).length;
      const alreadyCount = data.results.filter((r: any) => r.alreadyHad).length;
      toast({
        title: "Stripe webhook updated",
        description: updatedCount > 0
          ? `Added invoice.payment_failed to ${updatedCount} endpoint(s).`
          : `All endpoints already had this event.`,
      });
    },
    onError: (err: any) => {
      toast({ title: "Failed", description: err.message || "Could not update Stripe webhooks.", variant: "destructive" });
    },
  });
  
  const [referralBlastResult, setReferralBlastResult] = useState<{ total: number; sent: number; skipped: number; failed: number } | null>(null);
  const [referralBlastConfirm, setReferralBlastConfirm] = useState(false);
  const referralBlastMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/send-referral-blast");
      return res.json();
    },
    onSuccess: (data) => {
      setReferralBlastResult(data);
      setReferralBlastConfirm(false);
      toast({
        title: "Referral blast sent",
        description: `${data.sent} of ${data.total} emails sent successfully.`,
      });
    },
    onError: () => {
      setReferralBlastConfirm(false);
      toast({ title: "Failed", description: "Could not send referral blast.", variant: "destructive" });
    },
  });

  const [formData, setFormData] = useState({
    userField: "",
    brevoAttribute: "",
    isActive: true,
  });

  const { data: mappings = [], isLoading } = useQuery<BrevoFieldMapping[]>({
    queryKey: ["/api/admin/brevo-mappings"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/admin/brevo-mappings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brevo-mappings"] });
      toast({
        title: "Mapping created",
        description: "Brevo field mapping has been created successfully",
      });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Creation failed",
        description: error.message || "Failed to create mapping. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      return await apiRequest("PATCH", `/api/admin/brevo-mappings/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brevo-mappings"] });
      toast({
        title: "Mapping updated",
        description: "Brevo field mapping has been updated successfully",
      });
      setEditingMapping(null);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update mapping. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/admin/brevo-mappings/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/brevo-mappings"] });
      toast({
        title: "Mapping deleted",
        description: "Brevo field mapping has been deleted successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Deletion failed",
        description: error.message || "Failed to delete mapping. Please try again.",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      userField: "",
      brevoAttribute: "",
      isActive: true,
    });
  };

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!editingMapping) return;
    updateMutation.mutate({ id: editingMapping.id, ...formData });
  };

  const openEditDialog = (mapping: BrevoFieldMapping) => {
    setEditingMapping(mapping);
    setFormData({
      userField: mapping.userField,
      brevoAttribute: mapping.brevoAttribute,
      isActive: mapping.isActive,
    });
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this mapping?")) {
      deleteMutation.mutate(id);
    }
  };

  // Common user fields that can be mapped
  const userFieldOptions = [
    { value: "firstName", label: "First Name" },
    { value: "lastName", label: "Last Name" },
    { value: "email", label: "Email" },
    { value: "school", label: "School" },
    { value: "grade", label: "Grade" },
    { value: "gender", label: "Gender" },
    { value: "dateOfBirth", label: "Date of Birth" },
    { value: "parentFirstName", label: "Parent First Name" },
    { value: "parentLastName", label: "Parent Last Name" },
    { value: "parentEmail", label: "Parent Email" },
    { value: "parentPhone", label: "Parent Phone" },
    { value: "subscriptionStatus", label: "Subscription Status" },
    { value: "subscriptionPlan", label: "Subscription Plan" },
    { value: "subscriptionEndDate", label: "Subscription End Date" },
    { value: "subscribedGrade", label: "Subscribed Grade" },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading mappings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Automation Templates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wand2 className="h-4 w-4" />
            Automation Email Templates
          </CardTitle>
          <CardDescription>
            Create the 4 lifecycle email templates in Brevo (Welcome, Referral, Failed Payment, Cancellation).
            Run this once: re-running will create duplicates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={() => createTemplatesMutation.mutate()}
            disabled={createTemplatesMutation.isPending}
          >
            <Wand2 className="mr-2 h-4 w-4" />
            {createTemplatesMutation.isPending ? "Creating templates..." : "Create Automation Templates"}
          </Button>

          {templateIds && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Templates created successfully
              </div>
              <div className="rounded-md border bg-muted/40 p-3 space-y-1 text-sm font-mono">
                {Object.entries(templateIds).map(([name, id]) => (
                  <div key={name} className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground truncate">{name}</span>
                    <Badge variant="outline">ID: {id}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <div>
              <p className="text-sm font-medium">Stripe Webhook Event</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Adds <code className="bg-muted px-1 rounded">invoice.payment_failed</code> to all your Stripe webhook endpoints so failed payment emails fire automatically.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => addPaymentFailedMutation.mutate()}
              disabled={addPaymentFailedMutation.isPending}
            >
              <Settings className="mr-2 h-4 w-4" />
              {addPaymentFailedMutation.isPending ? "Updating Stripe..." : "Add payment_failed Event"}
            </Button>

            {stripeWebhookResults && (
              <div className="rounded-md border bg-muted/40 p-3 space-y-1 text-xs font-mono">
                {stripeWebhookResults.map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 shrink-0 text-green-500" />
                    <span className="truncate text-muted-foreground">{r.url}</span>
                    <Badge variant="outline" className="shrink-0">
                      {r.alreadyHad ? "already set" : "updated"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* One-time Referral Blast */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" />
            Referral Programme Blast
          </CardTitle>
          <CardDescription>
            Send the referral programme email to all current active subscribers who were on the platform before the automation was set up. Safe to run once. Do not repeat.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!referralBlastConfirm ? (
            <Button
              variant="outline"
              onClick={() => setReferralBlastConfirm(true)}
              disabled={referralBlastMutation.isPending || !!referralBlastResult}
            >
              <Mail className="mr-2 h-4 w-4" />
              {referralBlastResult ? "Blast already sent this session" : "Send Referral Email to All Active Subscribers"}
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This will email every active paid subscriber. Are you sure you want to continue?
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => referralBlastMutation.mutate()}
                  disabled={referralBlastMutation.isPending}
                >
                  {referralBlastMutation.isPending ? "Sending..." : "Yes, send now"}
                </Button>
                <Button variant="outline" onClick={() => setReferralBlastConfirm(false)} disabled={referralBlastMutation.isPending}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {referralBlastResult && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Blast completed
              </div>
              <div className="rounded-md border bg-muted/40 p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total subscribers</span><span>{referralBlastResult.total}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Emails sent</span><span className="text-green-600 dark:text-green-400 font-medium">{referralBlastResult.sent}</span></div>
                {referralBlastResult.skipped > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Skipped</span><span>{referralBlastResult.skipped}</span></div>}
                {referralBlastResult.failed > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Failed</span><span className="text-destructive">{referralBlastResult.failed}</span></div>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Brevo Field Mappings</h3>
          <p className="text-sm text-muted-foreground">
            Configure how user data is synced to Brevo contact attributes
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-mapping">
              <Plus className="mr-2 h-4 w-4" />
              New Mapping
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Brevo Field Mapping</DialogTitle>
              <DialogDescription>
                Map a user field to a Brevo contact attribute
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="userField">User Field</Label>
                <Select
                  value={formData.userField}
                  onValueChange={(value) => setFormData({ ...formData, userField: value })}
                >
                  <SelectTrigger data-testid="select-user-field">
                    <SelectValue placeholder="Select user field" />
                  </SelectTrigger>
                  <SelectContent>
                    {userFieldOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="brevoAttribute">Brevo Field Name</Label>
                <Input
                  id="brevoAttribute"
                  data-testid="input-brevo-field"
                  placeholder="e.g., FIRSTNAME, LASTNAME, SCHOOL"
                  value={formData.brevoAttribute}
                  onChange={(e) => setFormData({ ...formData, brevoAttribute: e.target.value.toUpperCase() })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must match the Brevo contact attribute name (case-sensitive)
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="isActive">Active</Label>
                <Switch
                  id="isActive"
                  data-testid="switch-active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateOpen(false);
                  resetForm();
                }}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formData.userField || !formData.brevoAttribute || createMutation.isPending}
                data-testid="button-save-mapping"
              >
                {createMutation.isPending ? "Creating..." : "Create Mapping"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {mappings.length === 0 ? (
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-muted-foreground">
                <Mail className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p>No field mappings configured</p>
                <p className="text-sm mt-1">Create a mapping to sync user data to Brevo</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          mappings.map((mapping) => (
            <Card key={mapping.id}>
              <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-base">
                      {userFieldOptions.find((o) => o.value === mapping.userField)?.label || mapping.userField}
                    </CardTitle>
                    <Badge variant={mapping.isActive ? "default" : "secondary"} data-testid={`badge-status-${mapping.id}`}>
                      {mapping.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(mapping)}
                    data-testid={`button-edit-${mapping.id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(mapping.id)}
                    data-testid={`button-delete-${mapping.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-sm">
                  <Settings className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Brevo Field:</span>
                  <code className="px-2 py-0.5 rounded bg-muted font-mono text-xs">
                    {mapping.brevoAttribute}
                  </code>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={!!editingMapping} onOpenChange={(open) => !open && setEditingMapping(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Brevo Field Mapping</DialogTitle>
            <DialogDescription>
              Update the mapping configuration
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-userField">User Field</Label>
              <Select
                value={formData.userField}
                onValueChange={(value) => setFormData({ ...formData, userField: value })}
              >
                <SelectTrigger data-testid="select-edit-user-field">
                  <SelectValue placeholder="Select user field" />
                </SelectTrigger>
                <SelectContent>
                  {userFieldOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-brevoAttribute">Brevo Field Name</Label>
              <Input
                id="edit-brevoAttribute"
                data-testid="input-edit-brevo-field"
                placeholder="e.g., FIRSTNAME, LASTNAME, SCHOOL"
                value={formData.brevoAttribute}
                onChange={(e) => setFormData({ ...formData, brevoAttribute: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-isActive">Active</Label>
              <Switch
                id="edit-isActive"
                data-testid="switch-edit-active"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditingMapping(null);
                resetForm();
              }}
              data-testid="button-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formData.userField || !formData.brevoAttribute || updateMutation.isPending}
              data-testid="button-edit-save"
            >
              {updateMutation.isPending ? "Updating..." : "Update Mapping"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
